<template>
  <AppShell
    role-label="Administrator"
    :nav-items="navItems"
    :active-view="activeView"
    :account="auth.user || {}"
    :collapsed="navCollapsed"
    @navigate="openView"
    @logout="signOut"
    @toggle-nav="navCollapsed = !navCollapsed"
  >
    <div class="admin-workspace">
      <PageHeader
        eyebrow="Administrator workspace"
        :title="activeView === 'experimentGroups' ? 'Experiment groups' : pageTitle"
        :description="activeView === 'experimentGroups' ? 'Assign students to A, B or C and check their Pre-test entry.' : pageDescription"
        :show-back="subPage"
        @back="backToList"
      />

      <div v-if="overviewMessage && activeView === 'overview'" class="ui-notice admin-notice" role="status">{{ overviewMessage }}</div>
      <div v-if="message" class="ui-notice admin-notice" role="status">{{ message }}</div>
      <AIBudgetNotice v-if="activeView === 'overview' || activeView === 'aiUsage'" />

      <section v-if="activeView === 'overview'" class="overview-stack">
        <MetricStrip :items="adminMetrics" />

        <div class="overview-grid">
          <article class="workspace-card attention-card">
            <SectionHeader title="Needs attention" description="A short operational queue drawn from current records and accounts." />
            <ul class="attention-list">
              <li v-for="item in attentionItems" :key="item.label">
                <div>
                  <strong>{{ item.value }}</strong>
                  <span>{{ item.label }}</span>
                </div>
                <button type="button" class="ui-button ui-button--quiet" @click="openView(item.view)">Review</button>
              </li>
            </ul>
          </article>

          <article class="workspace-card">
            <SectionHeader title="Writing status" description="Distribution across every student writing record." />
            <dl class="status-distribution">
              <div v-for="status in statuses" :key="status">
                <dt><StatusPill :value="status" /></dt>
                <dd>{{ statusCounts[status] }}</dd>
              </div>
            </dl>
          </article>
        </div>

        <article class="workspace-card recent-card">
          <SectionHeader title="Recent records" description="The five records with the latest update time.">
            <template #actions><button type="button" class="ui-button ui-button--quiet" @click="openView('records')">View all records</button></template>
          </SectionHeader>
          <div v-if="recentRecords.length" class="table-scroll">
            <table class="ui-table">
              <thead class="thead"><tr><th>Student</th><th>Assignment</th><th>Status</th><th>Updated</th><th><span class="sr-only">Action</span></th></tr></thead>
              <tbody>
                <tr v-for="row in recentRecords" :key="`${row.student.id}-${row.assignment.id}`">
                  <td data-label="Student"><strong>{{ displayName(row.student) }}</strong></td>
                  <td data-label="Assignment">{{ row.assignment.title }}</td>
                  <td data-label="Status"><StatusPill :value="row.status" /></td>
                  <td data-label="Updated">{{ formatDate(row.updated_at) }}</td>
                  <td data-label="Action"><button type="button" class="ui-button ui-button--quiet compact-button" :disabled="!row.id" @click="openRecord(row.id)">View</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="ui-empty">No writing records are available yet.</div>
        </article>
      </section>

      <section v-if="activeView === 'records'" class="workspace-card">
        <SectionHeader title="Writing records" description="Find student work by name, assignment, or workflow status." />
        <div class="list-toolbar">
          <input v-model="recordSearch" class="ui-input" aria-label="Search writing records" placeholder="Search records..." />
          <select v-model="recordStatus" class="ui-select filter-select" aria-label="Filter writing records by status"><option value="">All statuses</option><option v-for="status in statuses" :key="status" :value="status">{{ labelStatus(status) }}</option></select>
        </div>
        <div v-if="filteredRecords.length" class="table-scroll">
          <table class="ui-table">
            <thead class="thead"><tr><th>Student</th><th>Assignment</th><th>Class</th><th>Status</th><th>Updated</th><th>Action</th></tr></thead>
            <tbody><tr v-for="row in filteredRecords" :key="`${row.student.id}-${row.assignment.id}`"><td data-label="Student"><strong>{{ displayName(row.student) }}</strong></td><td data-label="Assignment">{{ row.assignment.title }}</td><td data-label="Class">{{ row.assignment.programme_class?.class_code || row.student.class_group || '-' }}</td><td data-label="Status"><StatusPill :value="row.status" /></td><td data-label="Updated">{{ formatDate(row.updated_at) }}</td><td data-label="Action"><button type="button" class="ui-button ui-button--primary compact-button" :disabled="!row.id" @click="openRecord(row.id)">View / Grade</button></td></tr></tbody>
          </table>
        </div>
        <div v-else class="ui-empty">No writing records match these filters.</div>
      </section>

      <section v-if="activeView === 'users'" class="workspace-card">
        <SectionHeader title="Users" description="Manage account access, roles, and dependent records.">
          <template #actions><button type="button" class="ui-button" @click="openView('experimentGroups')">Set experiment groups</button><button type="button" class="ui-button ui-button--primary" @click="openUserForm()">Create user</button></template>
        </SectionHeader>
        <div class="list-toolbar">
          <input v-model="userSearch" class="ui-input" aria-label="Search users" placeholder="Search users..." />
          <select v-model="roleFilter" class="ui-select filter-select" aria-label="Filter users by role"><option value="">All roles</option><option value="student">Students</option><option value="teacher">Teachers</option><option value="admin">Admins</option></select>
        </div>
        <div v-if="filteredUsers.length" class="table-scroll">
          <table class="ui-table">
            <thead class="thead"><tr><th>Name</th><th>Username</th><th>Role</th><th>Status</th><th>Dependencies</th><th>Action</th></tr></thead>
            <tbody><tr v-for="user in filteredUsers" :key="user.id"><td data-label="Name"><strong>{{ displayName(user) }}</strong></td><td data-label="Username">{{ user.username }}</td><td class="role-label" data-label="Role">{{ user.role }}</td><td data-label="Status"><StatusPill :value="user.is_active ? 'active' : 'inactive'" /></td><td data-label="Dependencies">{{ user.dependency_summary?.total || 0 }}</td><td data-label="Action"><button type="button" class="ui-button compact-button" @click="openUserForm(user)">Manage</button></td></tr></tbody>
          </table>
        </div>
        <div v-else class="ui-empty">No users match these filters.</div>
      </section>

      <section v-if="activeView === 'userForm'" class="workspace-card form-card">
        <SectionHeader title="Account details" description="Set the user identity, access role, and account state." />
        <form class="form-grid" @submit.prevent="saveUser">
          <FormField label="Username" required><input v-model="userForm.username" class="ui-input" required /></FormField>
          <FormField label="Password" :required="!editingUserId" helper="Use at least 8 characters."><input v-model="userForm.password" class="ui-input" type="password" :required="!editingUserId" placeholder="Minimum 8 characters" /></FormField>
          <FormField label="Display name"><input v-model="userForm.display_name" class="ui-input" /></FormField>
          <FormField label="Gender"><input v-model="userForm.gender" class="ui-input" /></FormField>
          <FormField label="Class label (not experiment group)" helper="This is old class information only. Set experiment A/B/C below; changing this field does not enable Pre-test."><input v-model="userForm.class_group" class="ui-input" placeholder="Class label only" /></FormField>
          <FormField label="Role"><select v-model="userForm.role" class="ui-select"><option value="student">Student</option><option value="teacher">Teacher</option><option value="admin">Admin</option></select></FormField>
          <label class="check-field"><input v-model="userForm.is_active" type="checkbox" /> <span><strong>Account is active</strong><small>Inactive accounts cannot sign in.</small></span></label>
          <div class="form-actions"><button class="ui-button ui-button--primary" type="submit">Save account</button><button v-if="editingUserId" class="ui-button ui-button--danger" type="button" @click="deleteUser">{{ currentUser?.deletion_mode === 'deactivate' ? 'Deactivate account' : 'Delete account' }}</button></div>
        </form>
        <p v-if="editingUserId && currentUser?.role === 'student'">Experimental A/B/C groups are managed inside each study. <RouterLink to="/admin/experiments">Open writing experiments</RouterLink></p>
      </section>

      <section v-if="activeView === 'assignmentBank'" class="workspace-card">
        <SectionHeader title="Assignment Bank" description="Maintain reusable task briefs for teachers to publish.">
          <template #actions><button type="button" class="ui-button ui-button--primary" @click="openBankForm()">Add assignment</button></template>
        </SectionHeader>
        <div class="list-toolbar"><input v-model="bankSearch" class="ui-input" aria-label="Search Assignment Bank" placeholder="Search Assignment Bank..." /></div>
        <div v-if="filteredBank.length" class="table-scroll">
          <table class="ui-table">
            <thead class="thead"><tr><th>Assignment</th><th>Task Brief</th><th>Status</th><th>Published uses</th><th>Action</th></tr></thead>
            <tbody><tr v-for="item in filteredBank" :key="item.id"><td data-label="Assignment"><strong>{{ item.title }}</strong></td><td class="truncate brief-column" data-label="Task Brief">{{ item.prompt }}</td><td data-label="Status"><StatusPill :value="item.is_active ? 'active' : 'archived'" /></td><td data-label="Published uses">{{ item.assignment_count }}</td><td data-label="Action"><button type="button" class="ui-button compact-button" @click="openBankForm(item)">Manage</button></td></tr></tbody>
          </table>
        </div>
        <div v-else class="ui-empty">No assignment briefs match this search.</div>
      </section>

      <section v-if="activeView === 'bankForm'" class="workspace-card form-card">
        <SectionHeader title="Assignment brief" description="Write the reusable title and instructions teachers will select." />
        <form class="form-grid form-grid--single" @submit.prevent="saveBankItem">
          <FormField label="Assignment title" required><input v-model="bankForm.title" class="ui-input" required /></FormField>
          <FormField label="Task Brief" required><textarea v-model="bankForm.prompt" class="ui-textarea brief-editor" rows="12" required /></FormField>
          <label v-if="editingBankId" class="check-field"><input v-model="bankForm.is_active" type="checkbox" /> <span><strong>Available for new publications</strong><small>Archived briefs remain attached to existing assignments.</small></span></label>
          <div class="form-actions"><button class="ui-button ui-button--primary" type="submit">Save assignment</button><button v-if="editingBankId" class="ui-button ui-button--danger" type="button" @click="deleteBankItem">{{ currentBankItem?.deletion_mode === 'archive' ? 'Archive assignment' : 'Delete assignment' }}</button></div>
        </form>
      </section>

      <section v-if="activeView === 'classes'" class="workspace-card">
        <SectionHeader title="Programme/Class" description="Organise cohorts and their teacher and student memberships.">
          <template #actions><button type="button" class="ui-button ui-button--primary" @click="openClassForm()">Create Programme/Class</button></template>
        </SectionHeader>
        <div class="list-toolbar"><input v-model="classSearch" class="ui-input" aria-label="Search Programme/Class" placeholder="Search Programme/Class..." /></div>
        <div v-if="filteredClasses.length" class="table-scroll">
          <table class="ui-table">
            <thead class="thead"><tr><th>Code</th><th>Class</th><th>Programme</th><th>Teachers</th><th>Students</th><th>Action</th></tr></thead>
            <tbody><tr v-for="item in filteredClasses" :key="item.id"><td data-label="Code"><strong>{{ item.class_code }}</strong></td><td data-label="Class">{{ item.class_name }}</td><td data-label="Programme">{{ item.programme_name || '-' }}</td><td data-label="Teachers">{{ item.teacher_count }}</td><td data-label="Students">{{ item.student_count }}</td><td data-label="Action"><button type="button" class="ui-button compact-button" @click="openClassForm(item)">Manage</button></td></tr></tbody>
          </table>
        </div>
        <div v-else class="ui-empty">No programme or class matches this search.</div>
      </section>

      <section v-if="activeView === 'classForm'" class="workspace-card form-card">
        <SectionHeader title="Programme/Class details" description="Set the class identity, term, and account membership." />
        <form class="form-grid" @submit.prevent="saveClass">
          <FormField label="Programme"><input v-model="classForm.programme_name" class="ui-input" /></FormField>
          <FormField label="Class name" required><input v-model="classForm.class_name" class="ui-input" required /></FormField>
          <FormField label="Class code" required><input v-model="classForm.class_code" class="ui-input" required /></FormField>
          <FormField label="Academic year"><input v-model="classForm.academic_year" class="ui-input" placeholder="2026/27" /></FormField>
          <FormField label="Semester"><input v-model="classForm.semester" class="ui-input" placeholder="Semester 1" /></FormField>
          <label class="check-field"><input v-model="classForm.is_active" type="checkbox" /> <span><strong>Programme/Class is active</strong><small>Inactive classes remain available in historical records.</small></span></label>
          <FormField class="form-wide" label="Teachers" helper="Select all teachers assigned to this class."><select v-model="classForm.teacher_ids" class="ui-select multi-select" multiple size="6"><option v-for="user in teachers" :key="user.id" :value="user.id">{{ displayName(user) }} · {{ user.username }}</option></select></FormField>
          <FormField class="form-wide" label="Students" helper="Select all students enrolled in this class."><select v-model="classForm.student_ids" class="ui-select multi-select" multiple size="9"><option v-for="user in studentUsers" :key="user.id" :value="user.id">{{ displayName(user) }} · {{ user.username }}</option></select></FormField>
          <div class="form-actions"><button class="ui-button ui-button--primary" type="submit">Save Programme/Class</button><button v-if="editingClassId" class="ui-button ui-button--danger" type="button" @click="deleteClass">{{ currentClass?.assignment_count || currentClass?.student_count || currentClass?.teacher_count ? 'Archive Programme/Class' : 'Delete Programme/Class' }}</button></div>
        </form>
      </section>

      <section v-if="activeView === 'profile'" class="workspace-card form-card profile-card">
        <SectionHeader title="Profile details" description="Update the name shown across the administrator workspace." />
        <div class="form-grid">
          <FormField label="Name"><input v-model="profileForm.display_name" class="ui-input" /></FormField>
          <FormField label="Gender"><input v-model="profileForm.gender" class="ui-input" /></FormField>
          <FormField label="Username" helper="Usernames are managed from the Users page."><input :value="auth.user?.username" class="ui-input" disabled /></FormField>
          <div class="form-actions"><button type="button" class="ui-button ui-button--primary" @click="saveProfile">Save profile</button></div>
        </div>
      </section>

      <section v-if="activeView === 'passwordRequests'" class="workspace-card">
        <AccountEmailAdminPanel />
        <SectionHeader title="Password reset requests" description="Issue one-time temporary passwords for student or teacher accounts." />
        <div v-if="temporaryPassword" class="ui-notice" role="status">Temporary password for <strong>{{ temporaryUsername }}</strong>: <code>{{ temporaryPassword }}</code>. Copy it now; it will not be shown again.</div>
        <div v-if="resetRequests.length" class="table-scroll"><table class="ui-table"><thead><tr><th>Username</th><th>Class note</th><th>Requested</th><th>Status</th><th>Action</th></tr></thead><tbody><tr v-for="item in resetRequests" :key="item.id"><td>{{ item.username }}</td><td>{{ item.class_note || '-' }}</td><td>{{ formatDate(item.created_at) }}</td><td><StatusPill :value="item.status" /></td><td><button class="ui-button ui-button--primary compact-button" type="button" :disabled="item.status !== 'pending'" @click="resolveReset(item)">Issue temporary password</button></td></tr></tbody></table></div>
        <div v-else class="ui-empty">No password reset requests are waiting.</div>
      </section>

      <section v-if="activeView === 'aiUsage'" class="workspace-card">
        <SectionHeader title="AI usage" description="Review model calls and token usage without storing student prompts or drafts.">
          <template #actions><button class="ui-button" type="button" @click="downloadAiUsage">Export CSV</button></template>
        </SectionHeader>
        <MetricStrip :items="aiUsageMetrics" />
        <p v-if="aiUsage.summary.unknown_calls">{{ aiUsage.summary.unknown_calls }} calls have unknown token usage. Totals below include reported values only.</p>
        <div v-if="aiUsage.events.length" class="table-scroll"><table class="ui-table"><thead><tr><th>Time</th><th>User</th><th>Feature</th><th>Model</th><th>Tokens</th><th>Status</th></tr></thead><tbody><tr v-for="item in aiUsage.events" :key="item.id"><td>{{ formatDate(item.created_at) }}</td><td>{{ item.username || '-' }}</td><td>{{ item.feature }}</td><td>{{ item.model }}</td><td>{{ tokenDisplay(item.input_tokens, item.output_tokens) }}</td><td><StatusPill :value="item.status" /></td></tr></tbody></table></div>
        <div v-else class="ui-empty">No AI usage has been recorded yet.</div>
      </section>
    </div>
  </AppShell>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '../api'
import AppShell from '../components/AppShell.vue'
import FormField from '../components/FormField.vue'
import AccountEmailAdminPanel from '../components/AccountEmailAdminPanel.vue'
import MetricStrip from '../components/MetricStrip.vue'
import PageHeader from '../components/PageHeader.vue'
import SectionHeader from '../components/SectionHeader.vue'
import StatusPill from '../components/StatusPill.vue'
import AIBudgetNotice from '../components/AIBudgetNotice.vue'
import { tokenDisplay } from '../ai-budget-helpers.mjs'
import dashboardHelpers from '../dashboard-helpers'
import { useAuthStore } from '../stores/auth'

const { requestJson } = dashboardHelpers
const auth = useAuthStore(); const router = useRouter(); const route = useRoute(); const activeView = ref('overview'); const message = ref(''); const overviewMessage = ref('')
const mobileViewport = window.matchMedia('(max-width: 767px)')
const navCollapsed = ref(window.matchMedia('(max-width: 767px)').matches)
const users = ref([]); const records = ref([]); const bank = ref([]); const classes = ref([])
const resetRequests = ref([]); const temporaryPassword = ref(''); const temporaryUsername = ref(''); const aiUsage = ref({ summary: { total_calls: 0, input_tokens: 0, output_tokens: 0 }, events: [] })
const userSearch = ref(''); const roleFilter = ref(''); const recordSearch = ref(''); const recordStatus = ref(''); const bankSearch = ref(''); const classSearch = ref('')
const editingUserId = ref(null); const editingBankId = ref(null); const editingClassId = ref(null)
const emptyUser = () => ({ username: '', password: '', display_name: '', gender: '', class_group: '', role: 'student', is_active: true }); const userForm = ref(emptyUser())
const emptyBank = () => ({ title: '', prompt: '', is_active: true }); const bankForm = ref(emptyBank())
const emptyClass = () => ({ programme_name: '', class_name: '', class_code: '', academic_year: '', semester: '', is_active: true, teacher_ids: [], student_ids: [] }); const classForm = ref(emptyClass())
const profileForm = ref({ display_name: '', gender: '' }); const statuses = ['not_started', 'in_progress', 'submitted', 'reviewed']
const overview = ref({
  metrics: { active_users: 0, programme_classes: 0, open_assignments: 0, writing_records: 0 },
  status_counts: Object.fromEntries(statuses.map(status => [status, 0])),
  attention: { awaiting_review: 0, inactive_accounts: 0, classes_without_open_assignments: 0 },
  recent_records: [],
})
const navItems = [{ view: 'overview', label: 'Overview' }, { view: 'records', label: 'Writing Records' }, { view: 'users', label: 'Users' }, { view: 'assignmentBank', label: 'Assignment Bank' }, { view: 'classes', label: 'Programme/Class' }, { view: 'passwordRequests', label: 'Password Requests' }, { view: 'aiUsage', label: 'AI Usage' }, { view: 'profile', label: 'Profile' }]
navItems.splice(3, 0, { view: 'experiments', label: 'Writing experiments' })
const subPage = computed(() => ['userForm', 'bankForm', 'classForm'].includes(activeView.value)); const pageTitle = computed(() => ({ overview: 'Overview', records: 'Writing Records', users: 'Users', userForm: editingUserId.value ? 'Edit User' : 'Create User', assignmentBank: 'Assignment Bank', bankForm: editingBankId.value ? 'Edit Assignment' : 'Add Assignment', classes: 'Programme/Class', classForm: editingClassId.value ? 'Edit Programme/Class' : 'Create Programme/Class', passwordRequests: 'Password Requests', aiUsage: 'AI Usage', profile: 'Profile' })[activeView.value])
const pageDescription = computed(() => ({ overview: 'Monitor the people, classes, assignments, and writing activity across the system.', records: 'Review writing progress and open individual records for grading.', users: 'Create accounts and manage access throughout the system.', userForm: editingUserId.value ? 'Update account identity, role, and access.' : 'Add a new account to the writing system.', assignmentBank: 'Maintain the reusable briefs teachers use to publish assignments.', bankForm: editingBankId.value ? 'Update this reusable assignment brief.' : 'Create a reusable assignment brief for teachers.', classes: 'Manage programmes, classes, and their memberships.', classForm: editingClassId.value ? 'Update this class and its memberships.' : 'Create a programme or class and assign its members.', passwordRequests: 'Process assisted account recovery requests.', aiUsage: 'Monitor safe AI usage metadata and token totals.', profile: 'Manage your administrator identity.' })[activeView.value])
const adminMetrics = computed(() => [
  { label: 'Users', value: overview.value.metrics.active_users, detail: 'Active accounts' },
  { label: 'Classes', value: overview.value.metrics.programme_classes, detail: 'Active Programme / Classes' },
  { label: 'Open assignments', value: overview.value.metrics.open_assignments, detail: 'Published class work' },
  { label: 'Writing records', value: overview.value.metrics.writing_records, detail: 'Student writings' },
])
const aiUsageMetrics = computed(() => [
  { label: 'Calls', value: aiUsage.value.summary.total_calls || 0 },
  { label: 'Known input tokens', value: aiUsage.value.summary.known_input_tokens ?? 'Unknown' },
  { label: 'Known output tokens', value: aiUsage.value.summary.known_output_tokens ?? 'Unknown' },
])
const statusCounts = computed(() => overview.value.status_counts)
const recentRecords = computed(() => overview.value.recent_records)
const attentionItems = computed(() => [
  { label: 'Submitted writings awaiting review', value: overview.value.attention.awaiting_review, view: 'records' },
  { label: 'Inactive user accounts', value: overview.value.attention.inactive_accounts, view: 'users' },
  { label: 'Classes without open assignments', value: overview.value.attention.classes_without_open_assignments, view: 'classes' },
])
const teachers = computed(() => users.value.filter(user => user.role === 'teacher' && user.is_active)); const studentUsers = computed(() => users.value.filter(user => user.role === 'student' && user.is_active)); const currentUser = computed(() => users.value.find(item => item.id === editingUserId.value)); const currentBankItem = computed(() => bank.value.find(item => item.id === editingBankId.value)); const currentClass = computed(() => classes.value.find(item => item.id === editingClassId.value))
const filteredUsers = computed(() => users.value.filter(user => (!roleFilter.value || user.role === roleFilter.value) && `${displayName(user)} ${user.username}`.toLowerCase().includes(userSearch.value.toLowerCase())))
const filteredRecords = computed(() => records.value.filter(row => (!recordStatus.value || row.status === recordStatus.value) && `${displayName(row.student)} ${row.assignment.title}`.toLowerCase().includes(recordSearch.value.toLowerCase())))
const filteredBank = computed(() => bank.value.filter(item => `${item.title} ${item.prompt}`.toLowerCase().includes(bankSearch.value.toLowerCase())))
const filteredClasses = computed(() => classes.value.filter(item => `${item.class_code} ${item.class_name} ${item.programme_name}`.toLowerCase().includes(classSearch.value.toLowerCase())))

onMounted(async () => { mobileViewport.addEventListener('change', syncNavigationForViewport); const requested = String(route.query.view || ''); if (['experiments', 'experimentGroups'].includes(requested)) return router.replace('/admin/experiments'); if (navItems.some(item => item.view === requested)) activeView.value = requested; await Promise.all([loadOverview(), loadUsers(), loadRecords(), loadBank(), loadClasses(), loadProfile()]) })
onBeforeUnmount(() => mobileViewport.removeEventListener('change', syncNavigationForViewport))
function syncNavigationForViewport(event) { navCollapsed.value = event.matches }
async function loadOverview() {
  overviewMessage.value = ''
  const result = await requestJson(apiFetch('overview'), 'Could not load overview.')
  if (!result.ok) {
    overviewMessage.value = result.error
    return
  }
  overview.value = result.data
}
async function loadUsers() { const res = await apiFetch('admin/users'); users.value = (await res.json()).users || [] }
async function loadRecords() { const res = await apiFetch('teacher/records'); records.value = (await res.json()).records || [] }
async function loadBank() { const res = await apiFetch('admin/assignment-bank'); bank.value = (await res.json()).assignment_bank || [] }
async function loadClasses() { const res = await apiFetch('programme-classes'); classes.value = (await res.json()).programme_classes || [] }
async function loadProfile() { const res = await apiFetch('auth/profile'); if (res.ok) { const data = (await res.json()).profile; profileForm.value = { display_name: data.display_name || '', gender: data.gender || '' } } }
function openView(view) { if (['experiments', 'experimentGroups'].includes(view)) return router.push('/admin/experiments'); activeView.value = view; message.value = ''; if (view === 'passwordRequests') loadResetRequests(); if (view === 'aiUsage') loadAiUsage() }
async function loadResetRequests() { const res = await apiFetch('password-reset-requests'); if (res.ok) resetRequests.value = (await res.json()).requests || [] }
async function resolveReset(item) { if (!window.confirm('This immediately changes the password and signs out existing sessions. Verify the account owner and deliver the temporary password privately. After changing it, the owner can bind their own email. Continue?')) return; const res = await apiFetch(`password-reset-requests/${item.id}/resolve`, { method: 'POST' }); const data = await res.json(); if (!res.ok) return message.value = data.error; temporaryPassword.value = data.temporary_password; temporaryUsername.value = item.username; await loadResetRequests() }
async function loadAiUsage() { const res = await apiFetch('admin/ai-usage'); if (res.ok) aiUsage.value = await res.json() }
async function downloadAiUsage() { const res = await apiFetch('admin/ai-usage?format=csv'); if (!res.ok) return message.value = 'Could not export AI usage.'; const blob = await res.blob(); const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'ai-usage.csv'; anchor.click(); URL.revokeObjectURL(url) }
function backToList() { activeView.value = editingUserId.value ? 'users' : editingBankId.value ? 'assignmentBank' : 'classes' }
function openUserForm(user = null) { editingUserId.value = user?.id || null; userForm.value = user ? { username: user.username, password: '', display_name: user.display_name || '', gender: user.gender || '', class_group: user.class_group || '', role: user.role, is_active: user.is_active } : emptyUser(); activeView.value = 'userForm' }
async function saveUser() { const endpoint = editingUserId.value ? `admin/users/${editingUserId.value}` : 'admin/users'; const res = await apiFetch(endpoint, { method: editingUserId.value ? 'PATCH' : 'POST', body: JSON.stringify(userForm.value) }); const data = await res.json(); if (!res.ok) return message.value = data.error; message.value = 'Account saved.'; await Promise.all([loadUsers(), loadOverview()]); activeView.value = 'users' }
async function deleteUser() { const user = currentUser.value; const deps = user?.dependency_summary?.total || 0; if (!window.confirm(`${user.deletion_mode === 'deactivate' ? 'Deactivate' : 'Delete'} “${user.username}”? ${deps ? `${deps} related records will be preserved.` : 'This empty account will be permanently deleted.'}`)) return; const res = await apiFetch(`admin/users/${user.id}`, { method: 'DELETE' }); const data = await res.json(); message.value = res.ok ? `Account ${data.action}.` : data.error; await Promise.all([loadUsers(), loadOverview()]); activeView.value = 'users' }
function openBankForm(item = null) { editingBankId.value = item?.id || null; bankForm.value = item ? { title: item.title, prompt: item.prompt, is_active: item.is_active } : emptyBank(); activeView.value = 'bankForm' }
async function saveBankItem() { const endpoint = editingBankId.value ? `admin/assignment-bank/${editingBankId.value}` : 'admin/assignment-bank'; const res = await apiFetch(endpoint, { method: editingBankId.value ? 'PATCH' : 'POST', body: JSON.stringify(bankForm.value) }); const data = await res.json(); if (!res.ok) return message.value = data.error; message.value = 'Assignment Bank saved.'; await loadBank(); activeView.value = 'assignmentBank' }
async function deleteBankItem() { const item = currentBankItem.value; if (!window.confirm(`${item.deletion_mode === 'archive' ? 'Archive' : 'Delete'} “${item.title}”? ${item.assignment_count ? `${item.assignment_count} published assignments will be preserved.` : 'This cannot be undone.'}`)) return; const res = await apiFetch(`admin/assignment-bank/${item.id}`, { method: 'DELETE' }); const data = await res.json(); message.value = res.ok ? `Assignment ${data.action}.` : data.error; await loadBank(); activeView.value = 'assignmentBank' }
async function openClassForm(item = null) { editingClassId.value = item?.id || null; if (!item) classForm.value = emptyClass(); else { const res = await apiFetch(`programme-classes/${item.id}`); const detail = (await res.json()).programme_class; classForm.value = { programme_name: detail.programme_name, class_name: detail.class_name, class_code: detail.class_code, academic_year: detail.academic_year, semester: detail.semester, is_active: detail.is_active, teacher_ids: detail.teachers.map(user => user.id), student_ids: detail.students.map(user => user.id) } } activeView.value = 'classForm' }
async function saveClass() { const endpoint = editingClassId.value ? `programme-classes/${editingClassId.value}` : 'programme-classes'; const res = await apiFetch(endpoint, { method: editingClassId.value ? 'PATCH' : 'POST', body: JSON.stringify(classForm.value) }); const data = await res.json(); if (!res.ok) return message.value = data.error; message.value = 'Programme/Class saved.'; await Promise.all([loadClasses(), loadOverview()]); activeView.value = 'classes' }
async function deleteClass() { const item = currentClass.value; if (!window.confirm(`${item.assignment_count || item.student_count || item.teacher_count ? 'Archive' : 'Delete'} “${item.class_code}”? Related work will be preserved.`)) return; const res = await apiFetch(`programme-classes/${item.id}`, { method: 'DELETE' }); const data = await res.json(); message.value = res.ok ? `Programme/Class ${data.action}.` : data.error; await Promise.all([loadClasses(), loadOverview()]); activeView.value = 'classes' }
async function saveProfile() { const res = await apiFetch('auth/profile', { method: 'PATCH', body: JSON.stringify(profileForm.value) }); const data = await res.json(); message.value = res.ok ? 'Profile saved.' : data.error }
function openRecord(id) { if (id) router.push(`/admin/records/${id}`) }
async function signOut() { await auth.logout(); router.replace('/login') }
function displayName(user) { return user?.display_name || user?.username || '-' } function labelStatus(value) { return ({ not_started: 'Not started', in_progress: 'In progress', submitted: 'Submitted', reviewed: 'Reviewed' })[value] || value || '-' } function formatDate(value) { return value ? new Date(value).toLocaleString() : '-' }
</script>

<style scoped>
.user-experiment-groups { margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--border); }
.admin-workspace {
  width: min(100%, 92rem);
  margin: 0 auto;
}

.admin-notice {
  margin-bottom: var(--space-6);
}

.overview-stack {
  display: grid;
  gap: var(--space-6);
}

.overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(18rem, 0.65fr);
  gap: var(--space-6);
}

.workspace-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-6);
  background: var(--surface);
}

.workspace-card > :deep(.section-header) {
  margin-bottom: var(--space-4);
}

.attention-card {
  border-top: 3px solid var(--accent);
}

.attention-list {
  display: grid;
  margin: 0;
  padding: 0;
  list-style: none;
}

.attention-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--border);
}

.attention-list li:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.attention-list div {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
}

.attention-list strong {
  min-width: 2ch;
  color: var(--accent-strong);
  font-family: var(--font-academic);
  font-size: 1.75rem;
  line-height: 1;
}

.attention-list span {
  color: var(--ink-muted);
  font-size: 0.875rem;
}

.status-distribution {
  display: grid;
  margin: 0;
}

.status-distribution div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--border);
}

.status-distribution div:last-child {
  border-bottom: 0;
}

.status-distribution dt,
.status-distribution dd {
  margin: 0;
}

.status-distribution dd {
  font-family: var(--font-academic);
  font-size: 1.25rem;
}

.list-toolbar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.list-toolbar .ui-input {
  flex: 1;
}

.filter-select {
  width: auto;
  min-width: 11rem;
}

.table-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
}

.ui-table {
  min-width: 48rem;
}

.ui-table th:first-child,
.ui-table td:first-child {
  padding-left: 0;
}

.ui-table th:last-child,
.ui-table td:last-child {
  padding-right: 0;
  text-align: right;
}

.ui-table tbody tr:last-child td {
  border-bottom: 0;
}

.compact-button {
  min-height: 2rem;
  padding: var(--space-1) var(--space-2);
  font-size: 0.8125rem;
}

.role-label {
  text-transform: capitalize;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brief-column {
  max-width: 28rem;
}

.form-card {
  max-width: 64rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-6);
}

.form-grid--single {
  grid-template-columns: minmax(0, 1fr);
}

.form-wide,
.form-actions {
  grid-column: 1 / -1;
}

.check-field {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding-top: 1.85rem;
  color: var(--ink);
  font-size: 0.875rem;
}

.check-field input {
  margin-top: 0.3rem;
}

.check-field span {
  display: grid;
  gap: var(--space-1);
}

.check-field small {
  color: var(--ink-muted);
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding-top: var(--space-2);
}

.brief-editor {
  min-height: 16rem;
}

.multi-select {
  min-height: 10rem;
}

@media (max-width: 960px) {
  .overview-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 767px) {
  .workspace-card {
    padding: var(--space-4);
  }

  .compact-button {
    min-width: 44px;
    min-height: 44px;
  }

  .list-toolbar,
  .form-grid {
    grid-template-columns: minmax(0, 1fr);
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .form-wide,
  .form-actions {
    grid-column: auto;
  }

  .check-field {
    padding-top: 0;
  }

  .table-scroll {
    overflow: visible;
  }

  .ui-table,
  .ui-table tbody {
    display: block;
    width: 100%;
    min-width: 0;
    background: transparent;
  }

  .ui-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .ui-table tbody tr {
    display: grid;
    width: 100%;
    gap: 0;
    margin-bottom: var(--space-3);
    border: 1px solid var(--border);
    border-left: 3px solid var(--accent);
    border-radius: var(--radius-surface);
    padding: var(--space-3);
    background: var(--surface);
  }

  .ui-table tbody tr:last-child {
    margin-bottom: 0;
  }

  .ui-table td,
  .ui-table th:first-child,
  .ui-table td:first-child,
  .ui-table th:last-child,
  .ui-table td:last-child {
    display: grid;
    grid-template-columns: minmax(6.5rem, 0.4fr) minmax(0, 1fr);
    align-items: start;
    gap: var(--space-3);
    border: 0;
    padding: var(--space-2) 0;
    text-align: left;
    white-space: normal;
  }

  .ui-table td::before {
    color: var(--ink-muted);
    content: attr(data-label);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .brief-column {
    max-width: none;
  }
}
</style>
